<template>
  <v-container fluid class="pa-0">
    <!-- Hero Section -->
    <v-row no-gutters>
      <v-col cols="12">
        <v-card
          class="hero-section d-flex align-center justify-center"
          :height="$vuetify.display.mdAndUp ? '80vh' : '70vh'"
          flat
          :color="$vuetify.theme.current.dark ? 'grey-darken-4' : 'primary'"
        >
          <v-container>
            <v-row justify="center" align="center">
              <v-col cols="12" md="8" lg="6" class="text-center">
                <v-fade-transition appear>
                  <div>
                    <Icon
                      name="material-symbols:warehouse"
                      :size="$vuetify.display.mdAndUp ? 120 : 80"
                      class="mb-6 hero-icon text-white"
                    />
                    <h1 class="hero-title text-white mb-4">
                      {{ $t('home.hero.title') }}
                    </h1>
                    <p class="hero-subtitle text-white mb-8">
                      {{ $t('home.hero.subtitle') }}
                    </p>
                    <div
                      class="d-flex flex-column flex-sm-row ga-4 justify-center"
                    >
                      <v-btn
                        v-if="!status || status === 'unauthenticated'"
                        size="large"
                        variant="elevated"
                        color="white"
                        class="text-primary px-8"
                        @click="navigateTo('/auth/login')"
                      >
                        <Icon name="mdi:login" class="mr-2" />
                        {{ $t('home.hero.get-started') }}
                      </v-btn>
                      <template v-else>
                        <v-btn
                          size="large"
                          variant="elevated"
                          color="white"
                          class="text-primary px-8"
                          @click="navigateTo('/storages')"
                        >
                          <Icon
                            name="material-symbols:warehouse"
                            class="mr-2"
                          />
                          {{ $t('home.hero.view-storages') }}
                        </v-btn>
                        <v-btn
                          v-if="isAdmin"
                          size="large"
                          variant="outlined"
                          color="white"
                          class="px-8"
                          @click="navigateTo('/admin')"
                        >
                          <Icon name="mdi:cog" class="mr-2" />
                          {{ $t('home.hero.admin-panel') }}
                        </v-btn>
                      </template>
                    </div>
                  </div>
                </v-fade-transition>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-col>
    </v-row>

    <!-- Statistics Section -->
    <v-container v-if="status === 'authenticated'" class="py-16">
      <v-row>
        <v-col cols="12" class="text-center mb-8">
          <h2 class="text-h3 font-weight-bold mb-4">
            {{ $t('home.overview.title') }}
          </h2>
          <p class="text-h6 text-medium-emphasis">
            {{ $t('home.overview.subtitle') }}
          </p>
        </v-col>
      </v-row>

      <v-row>
        <v-col v-for="stat in stats" :key="stat.title" cols="12" sm="6" md="3">
          <v-card elevation="2" hover class="text-center pa-4">
            <v-card-text>
              <div class="mb-3">
                <Icon :name="stat.icon" size="48" :color="stat.color" />
              </div>
              <h3
                class="text-h4 font-weight-bold mb-1"
                :style="{ color: stat.color }"
              >
                {{ stat.value || 0 }}
              </h3>
              <p class="text-body-1 font-weight-medium">
                {{ stat.title }}
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <!-- Quick Actions Section -->
    <v-container v-if="status === 'authenticated'" class="py-16">
      <v-card
        :title="$t('home.quick-actions.title')"
        class="pa-6"
        elevation="4"
        variant="tonal"
      >
        <v-row>
          <v-col
            v-for="action in quickActions"
            :key="action.title"
            cols="12"
            sm="6"
            md="3"
          >
            <v-card
              elevation="2"
              hover
              class="text-center pa-4 cursor-pointer"
              @click="navigateTo(action.route)"
            >
              <v-card-text>
                <Icon
                  :name="action.icon"
                  size="40"
                  :color="action.color"
                  class="mb-3"
                />
                <h4 class="text-h6 font-weight-bold mb-2">
                  {{ action.title }}
                </h4>
                <p class="text-body-2 text-medium-emphasis">
                  {{ action.description }}
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card>
    </v-container>
  </v-container>
</template>

<script setup lang="ts">
  import { useUserStore } from '~/stores/user'
  const { status } = useAuth()
  const userStore = useUserStore()

  // Initialize user role watcher
  userStore.initUserRoleWatcher()

  // Get admin status from store
  const isAdmin = userStore.isAdmin

  // Page meta
  definePageMeta({
    auth: false,
  })

  // Use Nuxt Head for SEO
  useHead(() => ({
    title: 'Storage Management System',
    meta: [
      {
        name: 'description',
        content:
          'Smart storage management system to organize, track, and manage your inventory across multiple storage locations.',
      },
    ],
  }))
  // Define types for the API responses
  interface StorageItem {
    id: number
    name: string
    description: string | null
    createdAt: Date | string
    updatedAt: Date | string
    items?: Array<{ quantity: number }>
    _count?: { items: number }
  }

  interface ItemData {
    id: number
    name: string
    description: string | null
    icon: string | null
    createdAt: Date | string
    updatedAt: Date | string
    storages?: Array<{ quantity: number }>
    _count?: { storages: number }
  }

  interface StatData {
    title: string
    value: number
    icon: string
    color: string
  }

  // Fetch statistics for authenticated users
  const stats = ref<StatData[]>([])
  const storagesData = ref<StorageItem[]>([])
  const itemsData = ref<ItemData[]>([])

  // Watch authentication status and fetch stats when user is authenticated
  watchEffect(async () => {
    if (status.value === 'authenticated') {
      try {
        const storagesResponse = await $fetch('/api/storages')
        const itemsResponse = await $fetch('/api/items') // Handle API response format (expecting { data: [...] })
        storagesData.value = (storagesResponse?.data ||
          storagesResponse ||
          []) as StorageItem[]
        itemsData.value = (itemsResponse?.data ||
          itemsResponse ||
          []) as ItemData[] // Calculate total items across all storages
        const totalItems = storagesData.value.reduce(
          (total: number, storage) => {
            return (
              total +
              (storage.items?.reduce(
                (sum: number, item) => sum + (item.quantity || 0),
                0
              ) || 0)
            )
          },
          0
        )

        // Calculate items with low stock (less than 5)
        const lowStockItems = itemsData.value.filter(item => {
          const totalQuantity =
            item.storages?.reduce(
              (sum: number, storage) => sum + (storage.quantity || 0),
              0
            ) || 0
          return totalQuantity < 5 && totalQuantity > 0
        }).length

        const { t } = useI18n()
        stats.value = [
          {
            title: t('home.overview.total-storages'),
            value: storagesData.value.length,
            icon: 'material-symbols:warehouse',
            color: 'primary',
          },
          {
            title: t('home.overview.total-items'),
            value: itemsData.value.length,
            icon: 'material-symbols:inventory-2',
            color: 'secondary',
          },
          {
            title: t('home.overview.total-quantity'),
            value: totalItems,
            icon: 'material-symbols:counter-1',
            color: 'success',
          },
          {
            title: t('home.overview.low-stock-alerts'),
            value: lowStockItems,
            icon: 'material-symbols:warning',
            color: 'warning',
          },
        ]
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching statistics:', error)
        // Set empty stats if there's an error
        stats.value = []
      }
    } else {
      // Clear stats when not authenticated
      stats.value = []
      storagesData.value = []
      itemsData.value = []
    }
  })

  const quickActions = computed(() => {
    const { t } = useI18n()
    const actions = [
      {
        title: t('home.quick-actions.view-storages.title'),
        description: t('home.quick-actions.view-storages.description'),
        icon: 'material-symbols:warehouse',
        color: 'primary',
        route: '/storages',
      },
      {
        title: t('home.quick-actions.browse-items.title'),
        description: t('home.quick-actions.browse-items.description'),
        icon: 'material-symbols:inventory-2',
        color: 'secondary',
        route: '/items',
      },
      {
        title: t('home.quick-actions.settings.title'),
        description: t('home.quick-actions.settings.description'),
        icon: 'material-symbols:settings',
        color: 'info',
        route: '/settings',
      },
    ]

    // Only add Admin Panel if user is admin
    if (isAdmin) {
      actions.splice(2, 0, {
        title: t('home.quick-actions.admin-panel.title'),
        description: t('home.quick-actions.admin-panel.description'),
        icon: 'material-symbols:admin-panel-settings',
        color: 'error',
        route: '/admin',
      })
    }

    return actions
  })
</script>

<style lang="scss" scoped>
  .hero-section {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    position: relative;
    overflow: hidden;
  }

  .hero-section::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(
        circle at 30% 20%,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 70% 80%,
        rgba(255, 255, 255, 0.05) 0%,
        transparent 50%
      );
  }

  .hero-icon {
    animation: float 3s ease-in-out infinite;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  .hero-title {
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 700;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .hero-subtitle {
    font-size: clamp(1rem, 2.5vw, 1.25rem);
    opacity: 0.9;
    max-width: 600px;
    margin: 0 auto;
  }

  .feature-card {
    transition:
      transform 0.3s ease,
      box-shadow 0.3s ease;
  }

  .feature-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  @media (max-width: 960px) {
    .hero-title {
      font-size: 2.5rem;
    }

    .hero-subtitle {
      font-size: 1.1rem;
    }
  }
</style>
